import { toast } from 'react-toastify';
import { User, Event, Registration, getEventRegistrations, getUnregisteredUsers as fetchUnregisteredUsers, registerUserForEvent, unregisterUserFromEvent } from '../../../services/eventService';
import apiClient from '../../../services/apiClient';

// Error handling utility
export const getErrorMessage = (error: any): string => {
  if (error.response && error.response.data && error.response.data.detail) {
    return error.response.data.detail;
  }
  return error.message || 'Ein unbekannter Fehler ist aufgetreten';
};

// Date formatting utility
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Get registered users for an event
export const getRegisteredUsers = (eventViewData: any): User[] => {
  const registeredUserIds = eventViewData.registrations.map((reg: Registration) => reg.User_ID);
  return eventViewData.users.filter((user: User) => registeredUserIds.includes(user.User_ID));
};

// Get unregistered users for an event
export const getUnregisteredUsers = async (eventId: string): Promise<User[]> => {
  try {
    return await fetchUnregisteredUsers(eventId);
  } catch (error) {
    console.error('Error fetching unregistered users:', error);
    toast.error(`Fehler beim Laden der nicht registrierten Benutzer: ${getErrorMessage(error)}`);
    return [];
  }
};

// Filter unregistered users based on search input
export const getFilteredUnregisteredUsers = (users: User[], searchInput: string): User[] => {
  if (!searchInput.trim()) return users;
  
  const lowerCaseSearch = searchInput.toLowerCase();
  return users.filter(user => 
    user.firstname.toLowerCase().includes(lowerCaseSearch) ||
    user.lastname.toLowerCase().includes(lowerCaseSearch) ||
    user.mail.toLowerCase().includes(lowerCaseSearch) ||
    (user.firm && user.firm.abbrev && user.firm.abbrev.toLowerCase().includes(lowerCaseSearch))
  );
};

// Handle user registration for event
export const handleRegisterUser = async (
  userId: string, 
  eventId: string, 
  setRegisteredUsers: React.Dispatch<React.SetStateAction<User[]>>,
  setUnregisteredUsers: React.Dispatch<React.SetStateAction<User[]>>,
  allUsers: User[]
): Promise<void> => {
  try {
    await registerUserForEvent(eventId, userId);
    
    // Find the user in all users
    const user = allUsers.find(u => u.User_ID === userId);
    if (!user) return;
    
    // Update the UI state
    setRegisteredUsers(prev => [...prev, user]);
    setUnregisteredUsers(prev => prev.filter(u => u.User_ID !== userId));
    
    toast.success(`${user.firstname} ${user.lastname} wurde erfolgreich für das Event registriert.`);
  } catch (error) {
    console.error('Error registering user:', error);
    toast.error(`Fehler bei der Registrierung: ${getErrorMessage(error)}`);
  }
};

// Handle user unregistration from event
export const handleUnregisterUser = async (
  userId: string, 
  eventId: string, 
  setRegisteredUsers: React.Dispatch<React.SetStateAction<User[]>>,
  setUnregisteredUsers: React.Dispatch<React.SetStateAction<User[]>>,
  allUsers: User[]
): Promise<void> => {
  try {
    await unregisterUserFromEvent(eventId, userId);
    
    // Find the user in all users
    const user = allUsers.find(u => u.User_ID === userId);
    if (!user) return;
    
    // Update the UI state
    setRegisteredUsers(prev => prev.filter(u => u.User_ID !== userId));
    setUnregisteredUsers(prev => [...prev, user]);
    
    toast.success(`${user.firstname} ${user.lastname} wurde erfolgreich vom Event abgemeldet.`);
  } catch (error) {
    console.error('Error unregistering user:', error);
    toast.error(`Fehler bei der Abmeldung: ${getErrorMessage(error)}`);
  }
};

// Download mail for a user
export const handleDownloadUserMail = (userId: string): void => {
  // Placeholder function to be implemented
  toast.info(`Mail für Benutzer ${userId} wird heruntergeladen...`);
};

// Implement other utility functions as needed
export const handleSendVotingEmail = (eventId: string): Promise<any> => {
  // Implementation
  console.log(`Sending voting email for event ${eventId}`);
  return Promise.resolve({ success: true, message: 'Emails sent successfully' });
};

export const handleDownloadPlan = (eventId: string): void => {
  console.log(`Downloading plan for event ${eventId}`);
};

export const handleDownloadExcel = async (eventId: string): Promise<void> => {
  try {
    await import('../../../services/eventService').then(module => {
      return module.downloadEventExcel(eventId);
    });
    toast.success('Excel file downloaded successfully');
  } catch (error) {
    console.error('Error downloading Excel:', error);
    toast.error(`Failed to download Excel: ${getErrorMessage(error)}`);
  }
};

export const handleDownloadVotes = (eventId: string): void => {
  console.log(`Downloading votes for event ${eventId}`);
};

export const handleGenerateMatches = async (eventId: string): Promise<boolean> => {
  try {
    await import('../../../services/eventService').then(module => {
      return module.generateGroupMatches(eventId);
    });
    toast.success('Group matches generated successfully');
    return true;
  } catch (error) {
    console.error('Error generating matches:', error);
    toast.error(`Failed to generate matches: ${getErrorMessage(error)}`);
    return false;
  }
};

export const handleDeleteGroup = (): void => {
  // Placeholder
};

export const handleRemoveUserFromGroup = (): void => {
  // Placeholder
};

export const handleAddUserToGroup = (): void => {
  // Placeholder
};

export const handleCopyVerificationCode = (code: string): void => {
  navigator.clipboard.writeText(code);
  toast.success("Verification code copied to clipboard!");
};

export const handleCopyRegisterLink = (eventId: string, verificationCode: string, baseUrl: string): void => {
  const link = `${baseUrl}/register/?event=${eventId}&verification_code=${verificationCode}`;
  navigator.clipboard.writeText(link);
  toast.success("Registration link copied to clipboard!");
};

export const handleCopyVoteLink = (eventId: string, baseUrl: string): void => {
  const link = `${baseUrl}/events/${eventId}/participants/`;
  navigator.clipboard.writeText(link);
  toast.success("Vote link copied to clipboard!");
};

export const handleCopySpotlightLink = (eventId: string, verificationCode: string, baseUrl: string): void => {
  const link = `${baseUrl}/spotlight/?event=${eventId}&verification_code=${verificationCode}`;
  navigator.clipboard.writeText(link);
  toast.success("Spotlight link copied to clipboard!");
};

export const handleDragStart = (): void => {
  // Placeholder
};

export const handleDragOver = (): void => {
  // Placeholder
};

export const handleDragEnter = (): void => {
  // Placeholder
};

export const handleDragLeave = (): void => {
  // Placeholder
};

export const handleDrop = (): void => {
  // Placeholder
};

export const handleShowAddUsersModal = (): void => {
  // Placeholder
};

export const handleToggleUserSelection = (): void => {
  // Placeholder
};

export const handleAddSelectedUsersToGroup = (): void => {
  // Placeholder
};

// Fetch unmatched users
export const fetchUnmatchedUsers = async (eventId: string, roundNumber?: number): Promise<any> => {
  try {
    const response = await apiClient.get(`/api/events/${eventId}/unmatched-users/${roundNumber || ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching unmatched users:', error);
    toast.error(`Error fetching unmatched users: ${getErrorMessage(error)}`);
    return { unmatched_users: [] };
  }
};
